import { useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CalendarX, CheckCircle2 } from "lucide-react";
import { APPOINTMENT_TYPE_LABELS } from "@clinic/types";
import { api } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/** Public page reached from the tokenized link in reminder emails. */
export function CancelAppointmentPage() {
  const { token } = useParams({ strict: false }) as { token: string };

  const appointment = useQuery({
    queryKey: ["public-appointment", token],
    queryFn: () => api.public_.getAppointmentByToken(token),
    retry: false,
  });

  const cancel = useMutation({
    mutationFn: () => api.public_.cancelByToken(token),
    onSuccess: () => appointment.refetch(),
  });

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <LogoFull iconSize={56} compact />
          </div>
          <Card>
            {appointment.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4" />
                <Skeleton className="h-10" />
              </div>
            ) : appointment.isError ? (
              <p className="py-4 text-center text-sm text-text-secondary">
                This cancellation link is invalid or has expired.
                <br />
                Please call the clinic if you need to change your appointment.
              </p>
            ) : appointment.data ? (
              appointment.data.status === "CANCELLED" ? (
                <div className="py-4 text-center">
                  <CheckCircle2 size={40} className="mx-auto mb-3 text-success" />
                  <h1 className="text-lg">Appointment cancelled</h1>
                  <p className="mt-2 text-sm text-text-secondary">
                    Your appointment has been cancelled. To book a new one, call{" "}
                    <span className="text-accent-light">{appointment.data.clinicPhone}</span>.
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="mb-1 text-lg">Cancel your appointment?</h1>
                  <p className="mb-4 text-sm text-text-secondary">
                    Hi {appointment.data.patientFirstName}, you are booked at{" "}
                    {appointment.data.clinicName}:
                  </p>
                  <div
                    className="mb-5 rounded-lg border bg-bg px-4 py-3 text-sm"
                    style={{ borderColor: "var(--accent-subtle)" }}
                  >
                    <div className="font-medium text-accent-light">
                      {APPOINTMENT_TYPE_LABELS[appointment.data.type]}
                    </div>
                    <div className="mt-1">
                      {format(new Date(appointment.data.startTime), "EEEE, d MMMM yyyy")} at{" "}
                      <span className="font-mono">
                        {format(new Date(appointment.data.startTime), "HH:mm")}
                      </span>
                    </div>
                  </div>
                  {cancel.isError && (
                    <p className="mb-3 text-sm text-danger">
                      Cancellation failed — please call the clinic.
                    </p>
                  )}
                  <Button
                    variant="danger"
                    className="w-full"
                    loading={cancel.isPending}
                    onClick={() => cancel.mutate()}
                  >
                    <CalendarX size={15} /> Yes, cancel this appointment
                  </Button>
                  <p className="mt-3 text-center text-xs text-text-tertiary">
                    Changed your mind? Just close this page — your appointment stays booked.
                  </p>
                </div>
              )
            ) : null}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
